import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import alertBox from '@/components/generics/alert-box/alert-box.vue';
import bankLinks from '@/components/wallet/bank-links/bank-links.vue';
import depositAmountOverview from '@/components/wallet/shared/deposit-amount-overview/deposit-amount-overview.vue';
import depositAmountPicker from '@/components/wallet/shared/deposit-amount-picker/deposit-amount-picker.vue';
import depositDisclaimer from '@/components/wallet/shared/deposit-disclaimer/deposit-disclaimer.vue';
import depositDoneLinks from '@/components/wallet/shared/deposit-done-links/deposit-done-links.vue';
import depositInfoOverview from '@/components/wallet/shared/deposit-info-overview/deposit-info-overview.vue';
import depositResult from '@/components/wallet/shared/deposit-result/deposit-result.vue';
import feeStatusMessage from '@/components/wallet/shared/fee-status-message/fee-status-message.vue';
import feeWaivedBanner from '@/components/wallet/shared/fee-waived-banner/fee-waived-banner.vue';
import promoCodeInput from '@/components/wallet/shared/promo-code-input/promo-code-input.vue';
import { DEPOSIT_METHOD_TYPE } from '@/components/wallet/shared/depositMethodTypes';
import defaultInstitutionLogo from '@/assets/icons/bank.svg';
import { formMixin, validators } from '@/components/forms/mixin';
import getValidationMessage from '@/helpers/getValidationMessage';
import { withMeta } from '@/helpers/dataLayer';
import depositWarningMixin from '@/mixins/depositWarningMixin';
import documentUploadMixin from '@/mixins/documentUpload';
import loaderMixin from '@/mixins/loader';
import popupPageModeMixin from '@/mixins/popupPageModeMixin';
import promoCodeMixin from '@/mixins/promoCode';
import { isNumber, roundToTwo } from '@/utils/helpers';

const DEPOSIT_CHIP_AMOUNTS = [10, 25, 50];

const STEPS = {
  SELECT_ACCOUNT: 1,
  ENTER_AMOUNT: 2,
  REVIEW: 3,
  SUCCESS: 4,
  FAILED: 5,
};

const FAILURE_REASONS = {
  ACCOUNT_PERMISSION_DENIED: 3,
  BANK_ACCOUNT_NOT_FOUND: 9,
  SUBMISSION_STATUS_UNCONFIRMED: 11,
  PROCESSING_ERROR: 12,
  INTERNAL_ERROR: 16,
  BANK_CONNECTION_REAUTHENTICATION_REQUIRED: 17,
};

const FAILURE_MESSAGES = {
  1: 'Identity verification is required before you can deposit.',
  2: 'This PredictIt account is not active.',
  3: 'Bank transfer deposits are not available for this account.',
  4: 'Please upload the requested documents before depositing.',
  5: 'Your uploaded documents are still being reviewed.',
  6: 'Enter a valid deposit amount.',
  7: 'This deposit exceeds your current deposit limit.',
  8: 'Please provide the requested identity information before depositing.',
  9: 'The selected bank account is no longer available. Choose or reconnect an account.',
  10: 'This deposit request could not be verified. Please review it and try again.',
  11: 'We could not confirm the final status of a recent deposit. Do not submit another deposit. Contact Support.',
  12: 'We could not process this deposit. Please try again.',
  13: 'The deposit request was invalid. Please review it and try again.',
  14: 'Deposits are temporarily unavailable while trading is suspended.',
  15: 'Your PredictIt account could not be found.',
  16: 'We could not process this deposit. Please try again later.',
  17: 'Sign in again to reconnect this bank account before depositing.',
  18: 'The selected bank account does not have enough available funds for this deposit.',
  19: 'The selected bank account is not eligible for this deposit.',
  20: 'We could not verify this bank account right now. Please try again later.',
  21: "We couldn't verify your bank balance right now. Please try again later.",
};

function errorData(error) {
  return get(error, 'response.data', {}) || {};
}

function responseMessage(data, fallback) {
  return data.errorMessage || data.message || data.Message ||
    FAILURE_MESSAGES[Number(data.failureReason)] || fallback;
}

export default {
  mixins: [
    depositWarningMixin,
    documentUploadMixin,
    formMixin,
    loaderMixin,
    popupPageModeMixin,
    promoCodeMixin,
  ],
  components: {
    alertBox,
    bankLinks,
    depositAmountOverview,
    depositAmountPicker,
    depositDisclaimer,
    depositDoneLinks,
    depositInfoOverview,
    depositResult,
    feeStatusMessage,
    feeWaivedBanner,
    promoCodeInput,
  },
  data() {
    return {
      amount: '',
      canDeposit: false,
      confirmedPromoAmount: 0,
      confirmedPromoCampaignName: '',
      currentStep: STEPS.SELECT_ACCOUNT,
      defaultInstitutionLogo,
      depositAmounts: DEPOSIT_CHIP_AMOUNTS,
      depositMethodType: DEPOSIT_METHOD_TYPE.TRANSFERS,
      errorMessage: '',
      failureMessage: '',
      failureReason: null,
      feeFreeAmount: 0,
      formDisclaimer: '',
      processingFeeCap: null,
      processingFeeAmount: '0.00',
      processingFeePercent: 0,
      promoReviewErrorMessage: '',
      reusableSubmissionToken: '',
      selectedAccount: null,
      submissionFailureNeedsIdempotentRetry: false,
      submissionToken: '',
      totalToWallet: '0.00',
      wasFeeDiscountApplied: false,
      STEPS,
    };
  },
  validations() {
    return {
      amount: validators.depositAmountCoinbase(
        this.minimumDepositAmount,
        this.maximumDepositAmount,
      ),
    };
  },
  computed: {
    ...mapGetters('app', ['isDepositPromoInputBoxEnabled', 'isLayoutMobile']),
    ...mapState('wallet', ['minimumDepositAmount']),
    ...mapState('user', ['ssnInfoVerified', 'status']),
    accountDescription() {
      if (!this.selectedAccount) return 'Bank account';
      const accountName = this.selectedAccount.accountName ||
        this.selectedAccount.accountType ||
        'Bank account';
      return this.selectedAccount.accountNumberLastFour
        ? `${accountName} - ${this.selectedAccount.accountNumberLastFour}`
        : accountName;
    },
    accountSummary() {
      if (!this.selectedAccount) return 'Bank account';
      return `${this.selectedAccount.institutionName || 'Bank account'} • ${this.accountDescription}`;
    },
    accountUploadDocumentTheme() {
      return this.isPage ? 'page-padded' : '';
    },
    amountHasError() {
      return this.$v.amount.$error;
    },
    amountValidationMessage() {
      return getValidationMessage(this.$v.amount);
    },
    classModifiers() {
      return {
        'add-funds-transfers': true,
        'add-funds-transfers--page': this.isPage,
        'add-funds-transfers--popup': this.isPopup,
      };
    },
    displayDisclaimer() {
      return this.formDisclaimer || this.disclaimer;
    },
    hasReviewPromo() {
      return this.confirmedPromoAmount > 0;
    },
    isAmbiguousSubmission() {
      return this.failureReason ===
        FAILURE_REASONS.SUBMISSION_STATUS_UNCONFIRMED;
    },
    isConfirmDisabled() {
      return this.invalidOrDisabled || !this.canDeposit;
    },
    maximumDepositAmount() {
      return 99999;
    },
    reviewPromoLabel() {
      return this.promoAppliedCode ||
        this.confirmedPromoCampaignName ||
        'Applied';
    },
    successAmount() {
      return `$${Number(this.amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  methods: {
    ...mapActions('wallet', [
      'confirmDepositBankTransfer',
      'getBalance',
      'getDepositBankTransferForm',
      'getDepositMethods',
      'submitDepositBankTransfer',
    ]),
    ...mapActions('popup', ['setCurrentPopup']),
    applyFeeSnapshot(data) {
      const feePercent = Number(data.feePercent || 0);
      const net = data.net === null || typeof data.net === 'undefined'
        ? Number(this.amount || 0)
        : Number(data.net);
      this.processingFeePercent = roundToTwo(feePercent * 100);
      this.processingFeeAmount = Number(Math.abs(data.fee || 0)).toFixed(2);
      this.totalToWallet = Number(
        net + this.confirmedPromoAmount,
      ).toFixed(2);
      this.wasFeeDiscountApplied = data.wasFeeDiscountApplied === true;
      this.feeFreeAmount = Number(data.feeFreeAmount || 0);
      this.processingFeeCap = data.feeCap === null ||
        typeof data.feeCap === 'undefined'
        ? null
        : Number(data.feeCap);
    },
    editAmount() {
      this.failureMessage = '';
      this.failureReason = null;
      // This remains only a reuse candidate until the server returns it from a new review.
      this.reusableSubmissionToken = this.submissionToken;
      this.submissionToken = '';
      this.currentStep = STEPS.ENTER_AMOUNT;
    },
    finishRequest() {
      this.loaderRemove();
      this.formEnable();
    },
    handleAmountBlur() {
      this.$v.amount.$touch();
      this.$gtm.trackEvent(withMeta({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'fill',
        label: 'Amount',
      }));
    },
    handleFormFailure(error) {
      const data = errorData(error);
      this.finishRequest();
      if (this.checkDocumentUploadFlags(data)) return;
      this.returnToAccounts(responseMessage(
        data,
        'We could not prepare this bank account for a deposit. Please try again.',
      ));
    },
    handleReviewFailure(error) {
      const data = errorData(error);
      this.finishRequest();
      if (this.checkDocumentUploadFlags(data)) return;
      this.failureReason = Number(data.failureReason) || null;
      this.failureMessage = responseMessage(
        data,
        'We could not review this deposit. Please try again.',
      );
      if (this.failureReason === FAILURE_REASONS.BANK_ACCOUNT_NOT_FOUND ||
        this.failureReason ===
          FAILURE_REASONS.BANK_CONNECTION_REAUTHENTICATION_REQUIRED) {
        this.returnToAccounts(this.failureMessage);
        return;
      }
      this.currentStep = STEPS.FAILED;
    },
    handleSubmitFailure(error) {
      const data = errorData(error);
      const responseStatus = Number(get(error, 'response.status') || 0);
      const failureReason = Number(data.failureReason) || null;
      this.submissionFailureNeedsIdempotentRetry = !get(error, 'response') ||
        failureReason === FAILURE_REASONS.INTERNAL_ERROR ||
        (responseStatus >= 500 && failureReason === null);
      this.finishRequest();
      if (this.checkDocumentUploadFlags(data)) return;
      this.failureReason = failureReason;
      this.failureMessage = responseMessage(
        data,
        'We could not complete this deposit. Please try again.',
      );
      if (this.failureReason === FAILURE_REASONS.BANK_ACCOUNT_NOT_FOUND ||
        this.failureReason ===
          FAILURE_REASONS.BANK_CONNECTION_REAUTHENTICATION_REQUIRED) {
        this.returnToAccounts(this.failureMessage);
        return;
      }
      this.currentStep = STEPS.FAILED;
    },
    isStep(name) {
      return this.currentStep === STEPS[name];
    },
    loadDepositForm() {
      if (!this.selectedAccount) return Promise.resolve();
      this.loaderAdd();
      this.formDisable();
      return this.getDepositBankTransferForm({
        linkedBankAccountId: this.selectedAccount.linkedBankAccountId,
        depositPromoCode: this.promoApplied ? this.promoAppliedCode : '',
      })
        .then((data) => {
          this.finishRequest();
          if (this.checkDocumentUploadFlags(data)) return;
          this.canDeposit = data.canDeposit === true;
          this.errorMessage = data.errorMessage || '';
          this.formDisclaimer = data.disclaimer || '';
          this.promoCodeDetail = data.promoCodeDetail || {};
          if (!this.canDeposit) {
            this.returnToAccounts(
              this.errorMessage || 'Bank transfer deposits are not available right now.',
            );
            return;
          }
          this.processingFeePercent = roundToTwo(
            Number(data.feePercent || 0) * 100,
          );
          this.processingFeeCap = data.feeCap === null ||
            typeof data.feeCap === 'undefined'
            ? null
            : Number(data.feeCap);
          this.errorMessage = '';
          this.currentStep = STEPS.ENTER_AMOUNT;
          this.$nextTick(() => this.$v.amount.$reset());
        })
        .catch(this.handleFormFailure);
    },
    onReviewAndConfirm() {
      this.$v.amount.$touch();
      if (!this.formIsValid() || !this.canDeposit || this.isFormDisabled) {
        return;
      }
      this.formDisable();
      this.$gtm.trackEvent(withMeta({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'submit',
        label: 'Submit Deposit',
      }));
      this.loaderAdd();
      this.confirmDepositBankTransfer({
        linkedBankAccountId: this.selectedAccount.linkedBankAccountId,
        amount: Number(this.amount),
        depositPromoCode: this.promoApplied ? this.promoAppliedCode : '',
        submissionToken: this.reusableSubmissionToken || null,
      })
        .then((data) => {
          this.finishRequest();
          if (this.checkDocumentUploadFlags(data)) return;
          if (!data.success || !data.canDeposit || !data.submissionToken) {
            this.failureReason = Number(data.failureReason) || null;
            this.failureMessage = responseMessage(
              data,
              'We could not review this deposit. Please try again.',
            );
            this.currentStep = STEPS.FAILED;
            return;
          }
          this.submissionToken = data.submissionToken;
          this.reusableSubmissionToken = data.submissionToken;
          this.confirmedPromoAmount = Number(data.promoAmount || 0);
          this.confirmedPromoCampaignName = data.promoCampaignName || '';
          this.promoReviewErrorMessage = data.promoErrorMessage || '';
          this.applyFeeSnapshot(data);
          this.currentStep = STEPS.REVIEW;
          this.scrollToTopIfPage();
        })
        .catch(this.handleReviewFailure);
    },
    onSubmitDeposit() {
      if (!this.selectedAccount || !this.submissionToken ||
        this.isFormDisabled) return;
      this.formDisable();
      this.$gtm.trackEvent(withMeta({
        event: 'eventTrackerTrigger',
        category: 'Deposit Confirm',
        action: 'submit',
        label: 'Confirm Deposit',
      }));
      this.loaderAdd();
      this.submitDepositBankTransfer({
        linkedBankAccountId: this.selectedAccount.linkedBankAccountId,
        amount: Number(this.amount),
        submissionToken: this.submissionToken,
      })
        .then((data) => {
          this.finishRequest();
          this.submissionFailureNeedsIdempotentRetry = false;
          if (!data.success) {
            this.failureReason = Number(data.failureReason) || null;
            this.failureMessage = responseMessage(
              data,
              'We could not complete this deposit. Please try again.',
            );
            this.currentStep = STEPS.FAILED;
            return;
          }
          this.applyFeeSnapshot(data);
          this.failureMessage = '';
          this.failureReason = null;
          this.submissionFailureNeedsIdempotentRetry = false;
          this.currentStep = STEPS.SUCCESS;
          this.$gtm.trackEvent(withMeta({
            event: this.status && this.status.hasBeenFunded
              ? 'MultiDeposit'
              : 'Deposit',
          }));
          this.$emit('success');
          this.getBalance().catch(() => null);
          this.getDepositMethods().catch(() => null);
          this.scrollToTopIfPage();
        })
        .catch(this.handleSubmitFailure);
    },
    onSuccessDone() {
      this.resetFlow();
      if (this.isPopup) {
        this.setCurrentPopup(null);
      }
    },
    onUpdateAmount(value) {
      if (this.amount === '') {
        this.amount = value.toFixed(2);
      } else if (isNumber(this.amount) || !isNaN(Number(this.amount))) {
        this.amount = (Number(this.amount) + value).toFixed(2);
      }
      this.$gtm.trackEvent(withMeta({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'click',
        label: `Amount ${value} Button`,
      }));
    },
    resetFlow() {
      this.amount = '';
      this.canDeposit = false;
      this.confirmedPromoAmount = 0;
      this.confirmedPromoCampaignName = '';
      this.currentStep = STEPS.SELECT_ACCOUNT;
      this.errorMessage = '';
      this.failureMessage = '';
      this.failureReason = null;
      this.feeFreeAmount = 0;
      this.formDisclaimer = '';
      this.processingFeeCap = null;
      this.processingFeeAmount = '0.00';
      this.promoReviewErrorMessage = '';
      this.reusableSubmissionToken = '';
      this.selectedAccount = null;
      this.submissionFailureNeedsIdempotentRetry = false;
      this.submissionToken = '';
      this.totalToWallet = '0.00';
      this.wasFeeDiscountApplied = false;
      this.onResetPromoCode();
      this.formEnable();
      this.$nextTick(() => this.$v.amount.$reset());
    },
    retryFailedRequest() {
      const failureReason = this.failureReason;
      const shouldRetrySameToken = this.submissionFailureNeedsIdempotentRetry &&
        this.submissionToken;
      this.failureMessage = '';
      this.failureReason = null;
      this.submissionFailureNeedsIdempotentRetry = false;

      // A missing response or an internal error does not establish whether the
      // provider accepted the first attempt. Replaying the same token is the only
      // safe retry because the server resolves it idempotently.
      if (shouldRetrySameToken) {
        this.onSubmitDeposit();
        return;
      }

      // A confirmed terminal provider result has consumed this intent. Review
      // it again so the server can rotate the token while reusing an eligible
      // same-bank, same-or-lower 15-minute assessment.
      if (failureReason === FAILURE_REASONS.PROCESSING_ERROR &&
        this.submissionToken) {
        this.reusableSubmissionToken = this.submissionToken;
        this.submissionToken = '';
        this.onReviewAndConfirm();
        return;
      }

      if (this.submissionToken) {
        this.editAmount();
        return;
      }

      this.currentStep = STEPS.ENTER_AMOUNT;
    },
    returnToAccounts(message) {
      this.amount = '';
      this.canDeposit = false;
      this.currentStep = STEPS.SELECT_ACCOUNT;
      this.errorMessage = message || '';
      this.failureMessage = '';
      this.failureReason = null;
      this.formDisclaimer = '';
      this.reusableSubmissionToken = '';
      this.selectedAccount = null;
      this.submissionFailureNeedsIdempotentRetry = false;
      this.submissionToken = '';
      this.formEnable();
      this.$nextTick(() => this.$v.amount.$reset());
    },
    scrollToTopIfPage() {
      if (this.isLayoutMobile && this.isPage) {
        setTimeout(() => window.scrollTo(0, 0), 100);
      }
    },
    selectAccount(account) {
      if (!account || !account.linkedBankAccountId) return;
      this.amount = '';
      this.errorMessage = '';
      this.failureMessage = '';
      this.failureReason = null;
      this.reusableSubmissionToken = '';
      this.selectedAccount = account;
      this.submissionFailureNeedsIdempotentRetry = false;
      this.submissionToken = '';
      this.loadDepositForm();
    },
  },
  watch: {
    ssnInfoVerified(value) {
      if (value && this.selectedAccount) {
        this.collectSsnInfo = false;
        this.loadDepositForm();
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/wallet/add-funds-transfers/add-funds-transfers.js